import { useState, type FormEvent } from "react"
import { useNavigate, Link } from "react-router-dom"
import { KeyRound, ArrowLeft } from "lucide-react"
import { changePassword } from "../../api/auth"
import { useAuth } from "../../hooks/useAuth"
import AuthCard, { AuthFormError } from "./AuthCard"

export default function ChangePasswordPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [currentPassword, setCurrentPassword] = useState("")
  const [password, setPassword] = useState("")
  const [passwordConfirmation, setPasswordConfirmation] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)

  const backPath = user?.role === "member" ? "/member" : "/dashboard"

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError("")

    if (password !== passwordConfirmation) {
      setError("Las contraseñas no coinciden.")
      return
    }
    if (password.length < 6) {
      setError("La nueva contraseña debe tener al menos 6 caracteres.")
      return
    }

    setLoading(true)
    try {
      await changePassword(currentPassword, password, passwordConfirmation)
      setSuccess(true)
      setTimeout(() => navigate(backPath, { replace: true }), 2000)
    } catch {
      setError("No se pudo cambiar la contraseña. Verifica tu contraseña actual.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthCard title="Cambiar Contraseña" subtitle="Ingresa tu contraseña actual y la nueva">
      {success ? (
        <div className="space-y-4">
          <div className="rounded-lg bg-primary/10 border border-primary/30 px-4 py-3 text-sm text-primary">
            Tu contraseña ha sido actualizada exitosamente. Redirigiendo...
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted uppercase tracking-wide">
              Contraseña actual
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="w-full rounded-lg border border-white/10 bg-surface-elevated px-4 py-3 text-white placeholder-muted/50 focus:border-primary"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted uppercase tracking-wide">
              Nueva contraseña
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full rounded-lg border border-white/10 bg-surface-elevated px-4 py-3 text-white placeholder-muted/50 focus:border-primary"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted uppercase tracking-wide">
              Confirmar nueva contraseña
            </label>
            <input
              type="password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              required
              minLength={6}
              className="w-full rounded-lg border border-white/10 bg-surface-elevated px-4 py-3 text-white placeholder-muted/50 focus:border-primary"
            />
          </div>

          <AuthFormError error={error} />

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-primary py-3 font-semibold text-black transition hover:opacity-90 glow-cyan disabled:opacity-50"
          >
            <KeyRound size={16} />
            {loading ? "Cambiando..." : "Cambiar contraseña"}
          </button>
        </form>
      )}

      <p className="mt-6 text-center text-sm text-muted">
        <Link
          to={backPath}
          className="flex items-center justify-center gap-1 text-primary hover:text-primary/80 transition"
        >
          <ArrowLeft size={14} />
          Volver
        </Link>
      </p>
    </AuthCard>
  )
}
